/**
 * MailChimp Lists Component
 * Manages MailChimp lists with comprehensive table view and integrated components
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { mailchimpService } from '../../services/mailchimpService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import './MailChimpLists.css';

const MailChimpLists = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();
  
  // State management
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLists, setSelectedLists] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [viewingList, setViewingList] = useState(null);
  
  // Statistics
  const [stats, setStats] = useState({
    total_lists: 0,
    active_lists: 0,
    default_lists: 0,
    synced_lists: 0,
    total_members: 0,
    unsubscribed_members: 0
  });

  // Load lists
  const loadLists = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await mailchimpService.getMailChimpLists();
      
      if (response.success) {
        const listsData = response.data || [];
        setLists(listsData);
        updateStatistics(listsData);
      } else {
        showError(response.message || 'Failed to load MailChimp lists', 'Error');
      }
    } catch (error) {
      showError('Failed to load MailChimp lists', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Update statistics
  const updateStatistics = (listsData) => {
    const stats = {
      total_lists: listsData.length,
      active_lists: listsData.filter(list => list.is_active).length,
      default_lists: listsData.filter(list => list.is_default).length,
      synced_lists: listsData.filter(list => list.sync_status === 'synced').length,
      total_members: listsData.reduce((sum, list) => sum + (list.member_count || 0), 0),
      unsubscribed_members: listsData.reduce((sum, list) => sum + (list.unsubscribe_count || 0), 0)
    };
    setStats(stats);
  };

  // Load lists on component mount
  useEffect(() => {
    loadLists();
  }, [loadLists]);

  // Handle create list
  const handleCreateList = async (listData) => {
    setIsProcessing(true);
    try {
      const response = await mailchimpService.createMailChimpList(listData);
      
      if (response.success) {
        showSuccess('MailChimp list created successfully', 'Success');
        setShowCreateForm(false);
        loadLists();
      } else {
        showError(response.message || 'Failed to create list', 'Error');
      }
    } catch (error) {
      showError('Failed to create list', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle update list
  const handleUpdateList = async (listId, listData) => {
    setIsProcessing(true);
    try {
      const response = await mailchimpService.updateMailChimpList(listId, listData);
      
      if (response.success) {
        showSuccess('MailChimp list updated successfully', 'Success');
        setEditingList(null);
        loadLists();
      } else {
        showError(response.message || 'Failed to update list', 'Error');
      }
    } catch (error) {
      showError('Failed to update list', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle delete list
  const handleDeleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) {
      return;
    }

    setIsProcessing(true);
    try {
      const response = await mailchimpService.deleteMailChimpList(listId);
      
      if (response.success) {
        showSuccess('MailChimp list deleted successfully', 'Success');
        loadLists();
      } else {
        showError(response.message || 'Failed to delete list', 'Error');
      }
    } catch (error) {
      showError('Failed to delete list', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle set default list
  const handleSetDefault = async (listId) => {
    setIsProcessing(true);
    try {
      const response = await mailchimpService.setDefaultList(listId);
      
      if (response.success) {
        showSuccess('Default list updated successfully', 'Success');
        loadLists();
      } else {
        showError(response.message || 'Failed to set default list', 'Error');
      }
    } catch (error) {
      showError('Failed to set default list', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };


  // Handle bulk sync members
  const handleBulkSyncMembers = async () => {
    if (!window.confirm('This will sync all active members to MailChimp. Continue?')) {
      return;
    }

    setIsProcessing(true);
    try {
      const response = await mailchimpService.bulkSyncMembers();
      
      if (response.success) {
        showSuccess(response.message || 'Bulk sync completed successfully', 'Success');
        loadLists();
      } else {
        showError(response.message || 'Bulk sync failed', 'Error');
      }
    } catch (error) {
      showError('Bulk sync failed', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle list selection
  const handleListSelect = (listId, isSelected) => {
    setSelectedLists(prev => 
      isSelected 
        ? [...prev, listId]
        : prev.filter(id => id !== listId)
    );
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedLists(lists.map(list => list.id));
    } else {
      setSelectedLists([]);
    }
  };

  // Handle view list details
  const handleViewList = (list) => {
    setViewingList(list);
  };

  // Handle edit list
  const handleEditList = (list) => {
    setEditingList(list);
  };

  // Handle sync individual list
  const handleSyncList = async (listId) => {
    setIsProcessing(true);
    try {
      const response = await mailchimpService.syncList(listId);
      
      if (response.success) {
        showSuccess('List synced successfully', 'Success');
        loadLists();
      } else {
        showError(response.message || 'Failed to sync list', 'Error');
      }
    } catch (error) {
      showError('Failed to sync list', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle bulk operations
  const handleBulkDelete = async () => {
    if (selectedLists.length === 0) {
      showError('Please select lists to delete', 'Error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedLists.length} selected lists?`)) {
      return;
    }

    setIsProcessing(true);
    try {
      const deletePromises = selectedLists.map(listId => 
        mailchimpService.deleteMailChimpList(listId)
      );
      
      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success).length;
      const failed = results.length - successful;

      if (successful > 0) {
        showSuccess(`${successful} lists deleted successfully`, 'Success');
      }
      if (failed > 0) {
        showError(`${failed} lists failed to delete`, 'Error');
      }
      
      setSelectedLists([]);
      loadLists();
    } catch (error) {
      showError('Failed to delete selected lists', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle bulk sync
  const handleBulkSync = async () => {
    if (selectedLists.length === 0) {
      showError('Please select lists to sync', 'Error');
      return;
    }

    setIsProcessing(true);
    try {
      const syncPromises = selectedLists.map(listId => 
        mailchimpService.syncList(listId)
      );
      
      const results = await Promise.allSettled(syncPromises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success).length;
      const failed = results.length - successful;

      if (successful > 0) {
        showSuccess(`${successful} lists synced successfully`, 'Success');
      }
      if (failed > 0) {
        showError(`${failed} lists failed to sync`, 'Error');
      }
      
      setSelectedLists([]);
      loadLists();
    } catch (error) {
      showError('Failed to sync selected lists', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mailchimp-lists">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="mailchimp-lists">
      {/* Header */}
      <div className="lists-header">
        <div className="header-content">
          <h2>📋 MailChimp Lists</h2>
          <p>Manage your MailChimp lists and member synchronization</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-outline"
            onClick={handleBulkSyncMembers}
            disabled={isProcessing || !canManageIntegrations}
          >
            {isProcessing ? 'Syncing...' : 'Bulk Sync Members'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
            disabled={!canManageIntegrations}
          >
            + Add List
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="lists-stats">
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>Total Lists</h3>
            <p className="stat-value">{stats.total_lists}</p>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Active Lists</h3>
            <p className="stat-value">{stats.active_lists}</p>
          </div>
        </div>
        <div className="stat-card default">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>Default Lists</h3>
            <p className="stat-value">{stats.default_lists}</p>
          </div>
        </div>
        <div className="stat-card synced">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <h3>Synced Lists</h3>
            <p className="stat-value">{stats.synced_lists}</p>
          </div>
        </div>
        <div className="stat-card members">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Members</h3>
            <p className="stat-value">{stats.total_members}</p>
          </div>
        </div>
        <div className="stat-card unsubscribed">
          <div className="stat-icon">📤</div>
          <div className="stat-content">
            <h3>Unsubscribed</h3>
            <p className="stat-value">{stats.unsubscribed_members}</p>
      </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedLists.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-info">
            <span className="selected-count">{selectedLists.length} list(s) selected</span>
          </div>
          <div className="bulk-buttons">
            <button
              className="btn btn-outline btn-sm"
              onClick={handleBulkSync}
              disabled={isProcessing || !canManageIntegrations}
            >
              Sync Selected
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={handleBulkDelete}
              disabled={isProcessing || !canManageIntegrations}
            >
              Delete Selected
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setSelectedLists([])}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Lists Table */}
      <div className="lists-content">
        {lists.length > 0 ? (
          <div className="lists-table-container">
            <div className="table-header">
              <h3>📊 MailChimp Lists</h3>
              <p>Complete list of all MailChimp lists with integrated actions</p>
            </div>
            
            <div className="scrollable-table-wrapper">
              <table className="lists-table">
                <thead>
                  <tr>
                    <th className="select-column">
                      <input
                        type="checkbox"
                        checked={selectedLists.length === lists.length && lists.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="select-all-checkbox"
                      />
                    </th>
                    <th className="list-name-column">List Name</th>
                    <th className="list-id-column">List ID</th>
                    <th className="status-column">Status</th>
                    <th className="members-column">Members</th>
                    <th className="sync-status-column">Sync Status</th>
                    <th className="default-column">Default</th>
                    <th className="created-column">Created</th>
                    <th className="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lists.map(list => (
                    <tr key={list.id} className="list-row">
                      <td className="select-cell">
                        <input
                          type="checkbox"
                          checked={selectedLists.includes(list.id)}
                          onChange={(e) => handleListSelect(list.id, e.target.checked)}
                          className="list-checkbox"
                        />
                      </td>
                      <td className="list-name-cell">
                        <div className="list-name-info">
                          <span className="list-name">{list.name || 'Unnamed List'}</span>
                          {list.description && (
                            <span className="list-description">{list.description}</span>
                          )}
                        </div>
                      </td>
                      <td className="list-id-cell">
                        <span className="list-id">{list.id}</span>
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${list.is_active ? 'active' : 'inactive'}`}>
                          {list.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="members-cell">
                        <div className="members-info">
                          <span className="member-count">{list.member_count || 0}</span>
                          {list.unsubscribe_count > 0 && (
                            <span className="unsubscribe-count">({list.unsubscribe_count} unsubscribed)</span>
                          )}
                        </div>
                      </td>
                      <td className="sync-status-cell">
                        <span className={`sync-status-badge ${list.sync_status || 'unknown'}`}>
                          {list.sync_status || 'Unknown'}
                        </span>
                      </td>
                      <td className="default-cell">
                        {list.is_default ? (
                          <span className="default-badge">⭐ Default</span>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleSetDefault(list.id)}
                            disabled={isProcessing || !canManageIntegrations}
                          >
                            Set Default
                          </button>
                        )}
                      </td>
                      <td className="created-cell">
                        <span className="created-date">
                          {list.created_at ? new Date(list.created_at).toLocaleDateString() : 'Unknown'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="list-actions">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleViewList(list)}
                            title="View list details"
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleEditList(list)}
                            disabled={!canManageIntegrations}
                            title="Edit list"
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleSyncList(list.id)}
                            disabled={isProcessing || !canManageIntegrations}
                            title="Sync list"
                          >
                            Sync
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteList(list.id)}
                            disabled={isProcessing || !canManageIntegrations}
                            title="Delete list"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No MailChimp Lists</h3>
          <p>Get started by syncing lists from MailChimp or creating a new one.</p>
          <div className="empty-actions">
            <button
              className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
              disabled={!canManageIntegrations}
            >
              Create New List
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Create List Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create New MailChimp List</h3>
              <button className="close-button" onClick={() => setShowCreateForm(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const listData = {
                  name: formData.get('name'),
                  description: formData.get('description'),
                  is_active: formData.get('is_active') === 'on'
                };
                handleCreateList(listData);
              }}>
                <div className="form-group">
                  <label htmlFor="name">List Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="form-input"
                    placeholder="Enter list name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-textarea"
                    placeholder="Enter list description"
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" name="is_active" defaultChecked />
                    <span>Active List</span>
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowCreateForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isProcessing}>
                    {isProcessing ? 'Creating...' : 'Create List'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit List Modal */}
      {editingList && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit MailChimp List</h3>
              <button className="close-button" onClick={() => setEditingList(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const listData = {
                  name: formData.get('name'),
                  description: formData.get('description'),
                  is_active: formData.get('is_active') === 'on'
                };
                handleUpdateList(editingList.id, listData);
              }}>
                <div className="form-group">
                  <label htmlFor="edit-name">List Name *</label>
                  <input
                    type="text"
                    id="edit-name"
                    name="name"
                    required
                    className="form-input"
                    defaultValue={editingList.name}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-description">Description</label>
                  <textarea
                    id="edit-description"
                    name="description"
                    className="form-textarea"
                    defaultValue={editingList.description}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" name="is_active" defaultChecked={editingList.is_active} />
                    <span>Active List</span>
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setEditingList(null)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isProcessing}>
                    {isProcessing ? 'Updating...' : 'Update List'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View List Modal */}
      {viewingList && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>📋 {viewingList.name} - List Details</h3>
              <button className="close-button" onClick={() => setViewingList(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="list-details">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">List ID:</span>
                      <span className="detail-value">{viewingList.id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">{viewingList.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Description:</span>
                      <span className="detail-value">{viewingList.description || 'No description'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`detail-value status-${viewingList.is_active ? 'active' : 'inactive'}`}>
                        {viewingList.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Default List:</span>
                      <span className="detail-value">{viewingList.is_default ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Sync Status:</span>
                      <span className={`detail-value sync-${viewingList.sync_status || 'unknown'}`}>
                        {viewingList.sync_status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="detail-section">
                  <h4>Member Statistics</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Total Members:</span>
                      <span className="detail-value">{viewingList.member_count || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Unsubscribed:</span>
                      <span className="detail-value">{viewingList.unsubscribe_count || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Created:</span>
                      <span className="detail-value">
                        {viewingList.created_at ? new Date(viewingList.created_at).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated:</span>
                      <span className="detail-value">
                        {viewingList.updated_at ? new Date(viewingList.updated_at).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setViewingList(null)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setViewingList(null);
                  handleEditList(viewingList);
                }}
                disabled={!canManageIntegrations}
              >
                Edit List
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailChimpLists;
